// src/lib/pharmacyFirst/pathways/aht/aht-wt-liraglutide-transfer.pathway.ts
//
// AHT Weight Management — Liraglutide (Saxenda®/Biolide®) Transfer Supply Pathway
// Source: AHT_Liraglutide_Clinical_Pathway.docx v1.0
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// For patients transferring from another provider already established on liraglutide.

import type { Pathway } from "../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q0_RED_FLAGS     = "lira-trans-q0-red-flags";
const Q1_F2F           = "lira-trans-q1-f2f";
const CONFIRM_CARD     = "lira-trans-confirmation-card";
const PGD_FRESH_CHECKS = "lira-trans-pgd-fresh-checks";
const Q3_INT_GLP1      = "lira-trans-int1-glp1";
const Q4_INT_INS       = "lira-trans-int2-insulin-sulph";
const Q5_INT_WARF      = "lira-trans-int3-warfarin";
const Q6_INT_NTI       = "lira-trans-int4-narrow-ti";
const Q7_CAUTIONS      = "lira-trans-cautions";
const Q8_TRANSFER_FLAG = "lira-trans-transfer-flag";
const Q9_DOSE          = "lira-trans-dose-select";
const Q10_ATTEST       = "lira-trans-attest";

const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtLiraglutideTransferPathway: Pathway = {
  id: "aht-wt-liraglutide-transfer",
  name: "Saxenda®/Biolide® (liraglutide) — Transfer Supply",
  population: "Adults and adolescents transferring from another provider already established on liraglutide",
  pgdRefs: ["pgd-aht-wt-liraglutide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q0_RED_FLAGS,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 0 — Red flags gateway
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q0_RED_FLAGS,
      type: "gateway_progression",
      triggersPayment: false,
      heading: "Check for red flags before proceeding",
      body:
        "Before proceeding with transfer supply, confirm the patient has none of the following:\n" +
        "• Severe or persistent abdominal pain (possible pancreatitis — refer urgently)\n" +
        "• Signs of allergic reaction or anaphylaxis — do not supply\n" +
        "• Pregnancy confirmed or suspected — discontinue\n" +
        "• New diagnosis of thyroid cancer, MEN2, or pancreatitis\n" +
        "• Clinically significant sustained increase in resting heart rate\n" +
        "• Gallstone symptoms (right upper quadrant pain, nausea, fever) — refer\n" +
        "If any red flag is present, do not proceed — refer to GP or A&E as appropriate.",
      next: Q1_F2F,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedFaceToFace"] === false,
          then: { outcomeId: OUT_NO_F2F },
        },
      ],
      default: "lira-trans-q-thyroid",
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Steps 2a–2g — Clinical screening (replaces confirmation card)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: "lira-trans-q-thyroid",
      type: "yes_no",
      text: "Does the patient have a personal or family history of thyroid cancer or MEN2?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "lira-trans-q-pancreatitis",
    },
    {
      id: "lira-trans-q-pancreatitis",
      type: "yes_no",
      text: "Does the patient have a history of pancreatitis (acute or chronic)?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "lira-trans-q-t1dm",
    },
    {
      id: "lira-trans-q-t1dm",
      type: "yes_no",
      text: "Does the patient have Type 1 or Type 3c diabetes mellitus?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "lira-trans-q-renal",
    },
    {
      id: "lira-trans-q-renal",
      type: "yes_no",
      text: "Does the patient have severe renal impairment (CrCl <30 mL/min) including end-stage renal disease?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "lira-trans-q-hepatic",
    },
    {
      id: "lira-trans-q-hepatic",
      type: "yes_no",
      text: "Does the patient have severe hepatic impairment?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "lira-trans-q-gi",
    },
    {
      id: "lira-trans-q-gi",
      type: "yes_no",
      text: "Does the patient have severe or unstable gastrointestinal disease including gastroparesis?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "lira-trans-q-pregnancy",
    },
    {
      id: "lira-trans-q-pregnancy",
      type: "yes_no",
      text: "Is the patient pregnant, breastfeeding, or planning to become pregnant?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2b — PGD inclusion / exclusion checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: PGD_FRESH_CHECKS,
      type: "pgd_exclusion_check",
      variant: "sequential",
      pgdIdOverride: "pgd-aht-wt-liraglutide",
      anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
      allCleared: Q3_INT_GLP1,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3 — Interaction checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q3_INT_GLP1,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedConcurrentWeightAgent"] === true,
          then: { outcomeId: OUT_CANNOT_SUPPLY },
        },
      ],
      default: Q4_INT_INS,
    },

    {
      id: Q4_INT_INS,
      type: "yes_no",
      text: "Is the patient currently using insulin or sulphonylureas?",
      hint: "Increased hypoglycaemia risk in T2DM — review and consider dose reduction before initiation.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the glucose management plan and any anticipated insulin or sulphonylurea dose reduction",
        acknowledgementText: "Patient has been counselled on hypoglycaemia risk and understands the management plan",
      },
      yes: Q5_INT_WARF,
      no: Q5_INT_WARF,
    },

    {
      id: Q5_INT_WARF,
      type: "yes_no",
      text: "Is the patient taking warfarin or other coumarin anticoagulants?",
      hint: "Delayed gastric emptying may affect absorption — monitor INR more frequently after initiation.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document INR monitoring plan and frequency",
        acknowledgementText: "Patient and GP/anticoagulation clinic have been informed of enhanced INR monitoring",
      },
      yes: Q6_INT_NTI,
      no: Q6_INT_NTI,
    },

    {
      id: Q6_INT_NTI,
      type: "yes_no",
      text: "Is the patient taking any oral medicines with a narrow therapeutic index?",
      hint: "Delayed gastric emptying may affect absorption — monitor where clinically relevant.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the monitoring and management plan for this interaction",
        acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
      },
      yes: Q7_CAUTIONS,
      no: Q7_CAUTIONS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 4 — Cautions review
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q7_CAUTIONS,
      type: "flag",
      variant: "warn",
      message:
        "Review cautions before supply:\n" +
        "• Pancreatitis: advise patients to report persistent severe abdominal pain; discontinue if suspected\n" +
        "• Heart rate: monitor resting HR; discontinue if clinically significant sustained increase\n" +
        "• Cholelithiasis/cholecystitis: advise right upper quadrant pain, nausea, fever → refer\n" +
        "• Dehydration and acute renal failure with GI effects — monitor CKD patients; encourage hydration\n" +
        "• Not a substitute for insulin\n" +
        "• Aspiration risk — advise anaesthetists/surgical team of liraglutide use before GA or sedation\n" +
        "• Rotate injection sites every injection — risk of cutaneous amyloidosis\n" +
        "• Adolescents: vomiting ~2× more frequent; monitor for disordered eating at every review",
      next: Q8_TRANSFER_FLAG,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Transfer notice
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q8_TRANSFER_FLAG,
      type: "flag",
      variant: "info",
      message:
        "Transfer from another provider — patient is already established on this medication.\n" +
        "Confirm the patient's current dose before supplying.\n" +
        "• Request evidence of previous supply (prescription, dispensing label, or transfer letter)\n" +
        "• Verify the dose matches the patient's stated current regimen\n" +
        "• Supply at the established dose — do not restart dose titration",
      next: Q9_DOSE,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Current dose selection
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q9_DOSE,
      type: "single_select",
      text: "What dose should be supplied?",
      options: [
        { id: "lira-0_6mg", label: "0.6 mg SC once daily" },
        { id: "lira-1_2mg", label: "1.2 mg SC once daily" },
        { id: "lira-1_8mg", label: "1.8 mg SC once daily" },
        { id: "lira-2_4mg", label: "2.4 mg SC once daily" },
        { id: "lira-3_0mg", label: "3.0 mg SC once daily" },
      ],
      routing: {
        "lira-0_6mg": Q10_ATTEST,
        "lira-1_2mg": Q10_ATTEST,
        "lira-1_8mg": Q10_ATTEST,
        "lira-2_4mg": Q10_ATTEST,
        "lira-3_0mg": Q10_ATTEST,
      },
      storesTo: "selectedDoseStep",
      required: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 7 — Supply attestation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q10_ATTEST,
      type: "attestation",
      text: "Confirm transfer supply of Saxenda®/Biolide® (liraglutide)",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied Saxenda®/Biolide® liraglutide pre-filled pen at the patient's confirmed established dose\n" +
        "• Verified the dose against evidence of previous supply\n" +
        "• Recorded BRAND NAME (Saxenda® or Biolide®) AND batch number (biological product — required at every supply)\n" +
        "• Checked cold chain integrity; pen must not have been frozen\n" +
        "• Supplied compatible needles separately\n" +
        "• Advised the patient to inform their GP of the provider transfer\n" +
        "• For adolescents: parent/guardian also understands technique and schedule",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    [OUT_NO_F2F]: {
      id: OUT_NO_F2F,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — face-to-face consultation required",
      reason: "Supply under this PGD requires the patient to attend in person at AHT registered premises.",
      safetyNetting: ["Ask the patient to book an in-person appointment to proceed with supply."],
    },
    [OUT_CANNOT_SUPPLY]: {
      id: OUT_CANNOT_SUPPLY,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — Saxenda®/Biolide® (liraglutide)",
      reason: "An inclusion criterion was not met or an exclusion criterion was triggered. Supply cannot proceed under this PGD.",
      safetyNetting: [
        "Discuss the reason supply cannot proceed with the patient.",
        "Consider referral to prescriber or GP if clinically appropriate.",
      ],
    },
    [OUT_SUPPLY]: {
      id: OUT_SUPPLY,
      type: "outcome",
      outcomeType: "supply",
      title: "Transfer supply complete — liraglutide (Saxenda®/Biolide®)",
      reason: "All inclusion criteria met, no exclusion criteria triggered, and transfer supply attestation confirmed.",
      drugRef: "liraglutide",
      safetyNetting: [
        "Return in 4 weeks for monitoring review.",
        "Missed dose: if within 12 hours of usual time — take now. If less than 12 hours to next dose — skip.",
        "Pancreatitis warning: severe abdominal pain → stop injecting, attend A&E.",
        "Gallstones: right upper quadrant pain, nausea, fever → seek advice.",
      ],
    },
  },
};
